import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { COLUMNS } from "./Column";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSortAmountUpAlt
} from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import "./accUserListTabletable.scss";
import { loadAccountUserDataThunk } from "../../../../../redux/accountingSoftwareSlice";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import { useDispatch, useSelector } from "react-redux";
import { noDataPresent, responseStatus } from "../../../../../locales/en";
import {
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType
} from "../../../../../utils/constants/appConstants";
import { showToast } from "../../../../../utils/utils";
import DualPagination from "../../../../common/DualPagination";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return "";
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const AccUserListTable = ({ roleId, isOpen }) => {
  const columns = useMemo(() => COLUMNS, []);

  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedRows, setExpandedRows] = useState([]);

  const { loadingAccountUserData } = useSelector(
    (state) => state.accountingSoftware
  );

  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const dispatch = useDispatch();

  const loadAccouningSoftware = async (pageNumber, pageSize, roleId) => {
    const params = {
      page: pageNumber,
      size: pageSize
    };

    try {
      const response = await dispatch(
        loadAccountUserDataThunk({ id: roleId, payload: params })
      ).unwrap();

      const filteredData = response.data?.data ?? [];
      const connectionDetails = filteredData?.[0]?.userConnectionDetails || [];

      const filteredConnectionDetails = connectionDetails.filter((item) => {
        const first = item?.firstName?.toString()?.trim();
        const last = item?.lastName?.toString()?.trim();
        return Boolean(first || last);
      });

      setData(filteredConnectionDetails);

      setTotalPages(response?.data?.pageInfo?.totalPages || 0);
    } catch (error) {
      showToast(
        error.response?.data?.message || responseStatus.errorText,
        showToastType.error
      );
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setPageSize
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex, pageSize } = state;

  useEffect(() => {
    if (isOpen) {
      loadAccouningSoftware(pageIndex, pageSize, roleId);
    }
  }, [pageIndex, pageSize, roleId, isOpen]);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  return (
    <>
      <table
        className="mt-2 mb-3 rolemodal-table bgless_table"
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" key={key} {...rest}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                  >
                    <span>{renderSortIcon(column)}</span>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loadingAccountUserData && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!loadingAccountUserData && page.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!loadingAccountUserData &&
            page.length > 0 &&
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>

                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps()}
                      >
                        {cell.column.id === "status" ? (
                          <span
                            className={`badge border border-${
                              cell.value === "connected" ? "success" : "danger"
                            } rounded-pill text-${
                              cell.value === "connected" ? "success" : "danger"
                            }`}
                          >
                            {cell.value === "connected"
                              ? "Connected"
                              : "Disconnected"}
                          </span>
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns
                            .filter((col) => col.accessor)
                            .map((column) => {
                              const safeValue = row.original?.[column.accessor];
                              return (
                                <span
                                  key={column.accessor}
                                  data-label={column.Header}
                                >
                                  {safeValue}
                                </span>
                              );
                            })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>

      {data && data?.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data?.length}
        />
      )}
    </>
  );
};

AccUserListTable.propTypes = {
  roleId: PropTypes.any,
  isOpen: PropTypes.bool.isRequired
};

export default AccUserListTable;
